'use client';

import {
  ArchiveBoxIcon,
  BookOpenIcon,
  FilmIcon,
  HeartIcon,
  PlayCircleIcon,
} from '@heroicons/react/24/outline';
import { useLocale, useTranslations } from 'next-intl';
import React, {
  createContext,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { HeroIcon } from '@/components/atoms';
import {
  addTrackToPlaylist,
  createUserPlaylist,
  deleteUserPlaylist,
  fetchAllFavouriteIds,
  fetchRecentlyListened,
  fetchSavedAlbums,
  fetchSavedArticles,
  fetchSavedCollections,
  fetchSavedPlaylists,
  fetchSavedTracks,
  fetchSavedVideos,
  fetchUserCreatedPlaylists,
  fetchPersonalPlaylist,
  removeTrackFromPlaylist,
  renameUserPlaylist,
  reorderPlaylistTracks,
  setFavourite,
} from '@/lib/api';
import { useAuthContext } from '@/lib/contexts/auth';
import { logger } from '@/lib/logger';

import { FavoritesFilter, FavouriteType } from './const';
import { buildPersonalPlaylists, buildTrackVirtualPlaylists } from './utils';

import type {
  FavouriteContent,
  FavouriteList,
  FavouriteMap,
  FavoritesContextType,
  LibraryContent,
  UserPlaylist,
  librarySectionsMap,
} from './types';
import type { TabConfig } from '@/components/organisms';

import type { PersonalPlaylist, PlaylistTrack } from '@/lib/api/types';

/**
 * Extend the context value with the MAP form so components can:
 * - do O(1) checks
 * - use it in deps to re-render immediately on optimistic toggle
 */
export type FavoritesContextValue = FavoritesContextType & {
  favouritesMap: FavouriteMap;
};

type FavouriteIds = Partial<Record<FavouriteType, number[]>>;

export const FavoritesContext = createContext<FavoritesContextValue | null>(
  null,
);

function arrayToRecord<
  T extends {
    id?: string | number;
    trackId?: string | number;
    created_at?: string;
  },
>(
  arr: T[] | unknown,
  sortKey: 'id' | 'trackId' | 'created_at' = 'id',
): Record<string, T> {
  if (!Array.isArray(arr)) return {};

  return arr.reduce(
    (acc, item) => {
      const key = item[sortKey];
      if (key !== undefined && key !== null && `${key}` !== '') {
        acc[`${key}`] = item;
      }
      return acc;
    },
    {} as Record<string, T>,
  );
}

function recordToArray<T extends object>(record: Record<string, T>): T[] {
  if (!record) return [];
  return Object.values(record).filter(Boolean);
}

export function FavoritesProvider({ children }: { children: ReactNode }) {
  const t = useTranslations();
  const locale = useLocale();
  const { isAuthenticated } = useAuthContext();

  const [favouritesMap, setFavouritesMap] = useState<FavouriteMap>(() =>
    Object.values(FavouriteType).reduce((acc, type) => {
      acc[type] = {};
      return acc;
    }, {} as FavouriteMap),
  );

  const [userPlaylists, setUserPlaylists] = useState<UserPlaylist[]>([]);
  const [recentlyListenedTracks, setRecentlyListenedTracks] = useState<
    PlaylistTrack[]
  >([]);
  const [defaultRecentlyListenedTracksSize] = useState<number>(20);

  const [
    totalRecentlyListenedTracksCount,
    setTotalRecentlyListenedTracksCount,
  ] = useState<number>(0);
  const [
    totalRecentlyListenedTracksPages,
    setTotalRecentlyListenedTracksPages,
  ] = useState<number>(0);

  const isLatestFavouritesLoadedRef = useRef(false);
  // Synchronous in-flight guard — using a ref instead of state so the check
  // is immediate and cannot be bypassed by concurrent calls racing before
  // a setLoading(true) state update is committed.
  const isHydratingRef = useRef(false);
  const [loading, setLoading] = useState(false);

  const isFavourite = useCallback(
    (type: FavouriteType, id: number): boolean =>
      Boolean(favouritesMap[type]?.[id]),
    [favouritesMap],
  );

  const refreshSpecificFavouriteType = useCallback(
    async (type: FavouriteType): Promise<void> => {
      try {
        let data: unknown[] = [];

        switch (type) {
          case FavouriteType.Track:
            data = await fetchSavedTracks(locale);
            break;
          case FavouriteType.Album:
            data = await fetchSavedAlbums(locale);
            break;
          case FavouriteType.Playlist:
            data = await fetchSavedPlaylists(locale);
            break;
          case FavouriteType.Article:
            data = await fetchSavedArticles(locale);
            break;
          case FavouriteType.Video:
            data = await fetchSavedVideos(locale);
            break;
          case FavouriteType.Collection:
            data = await fetchSavedCollections(locale);
            break;
          default:
            return;
        }

        setFavouritesMap(prev => ({
          ...prev,
          [type]: arrayToRecord(
            data as Array<{ id?: string | number }>,
            'id',
          ) as Record<string, FavouriteContent>,
        }));
      } catch (err) {
        logger.error(err, {
          context: `[Favorites] Failed to refresh ${type} favourites`,
        });
      }
    },
    [locale],
  );

  const syncWithAPI = useCallback(
    async (type: FavouriteType, id: number, enabled: boolean) => {
      const res = await setFavourite({ type, id, enabled, lang: locale });

      await refreshSpecificFavouriteType(type);

      return res;
    },
    [locale, refreshSpecificFavouriteType],
  );

  const toggleFavourite = useCallback(
    (
      type: FavouriteType,
      id: number,
      _lang: string,
      content?: FavouriteContent,
    ): void => {
      const isFav = Boolean(favouritesMap[type]?.[id]);
      const enabled = !isFav;

      if (enabled) {
        isLatestFavouritesLoadedRef.current = true;
        setTimeout(() => {
          isLatestFavouritesLoadedRef.current = false;
        }, 500);
      }

      // optimistic update
      setFavouritesMap(prev => {
        const updated = { ...(prev[type] ?? {}) };
        if (isFav) {
          delete updated[id];
        } else {
          updated[id] = content ?? ({} as FavouriteContent);
        }

        return { ...prev, [type]: updated };
      });

      void syncWithAPI(type, id, enabled).catch(err => {
        logger.error(err, {
          context: `[Favorites] Sync failed for ${type}:${id}`,
        });
      });
    },
    [favouritesMap, syncWithAPI],
  );

  const fetchUserPlaylists = async (): Promise<void> => {
    try {
      const data = await fetchUserCreatedPlaylists(locale);
      setUserPlaylists(data);
    } catch (err) {
      logger.error(err, {
        context: '[Favorites] Failed to fetch playlists',
      });
    }
  };

  const addTrackToUserPlaylist = async (
    playlistId: number,
    trackId: number,
  ) => {
    try {
      await addTrackToPlaylist(playlistId, trackId);
    } catch (err) {
      logger.error(err, {
        context: `[Favorites] Failed to add track ${trackId} to playlist ${playlistId}`,
      });
    }
  };

  const removeTrackFromUserPlaylist = async (
    playlistId: number,
    trackId: number,
  ) => {
    try {
      await removeTrackFromPlaylist(playlistId, trackId);
    } catch (err) {
      logger.error(err, {
        context: `[Favorites] Failed to remove track ${trackId} from playlist ${playlistId}`,
      });
    }
  };

  const createUserPlaylistAndStore = async (
    name: string,
  ): Promise<UserPlaylist> => {
    const newPlaylist = await createUserPlaylist(name);
    setUserPlaylists(prev => [...prev, newPlaylist]);
    return newPlaylist;
  };

  const renameUserPlaylistAndStore = async (
    playlistId: number,
    newName: string,
  ) => {
    try {
      await renameUserPlaylist(playlistId, newName);
      await fetchUserPlaylists();
    } catch (err) {
      logger.error(err, { context: '[Favorites] Failed to rename playlist' });
      await fetchUserPlaylists();
    }
  };

  const deleteUserPlaylistAndStore = async (playlistId: number) => {
    try {
      await deleteUserPlaylist(playlistId);
      setUserPlaylists(prev => prev.filter(p => p.id !== playlistId));
    } catch (err) {
      logger.error(err, { context: '[Favorites] Failed to delete playlist' });
    }
  };

  const reorderPlaylistTracksAndStore = async (
    playlistId: string | number,
    songsOrder: string[],
  ) => {
    try {
      await reorderPlaylistTracks(playlistId, songsOrder);
    } catch (err) {
      logger.error(err, {
        context: '[Favorites] Failed to reorder tracks of playlist',
      });
    }
  };

  const fetchPersonalPlaylistWithTracks = async (
    playlistId: string | number,
  ): Promise<PersonalPlaylist | null> => {
    try {
      const numericId =
        typeof playlistId === 'string' && playlistId.includes('/')
          ? playlistId.split('/')[1]
          : playlistId;

      if (numericId == null || `${numericId}` === '') return null;

      return await fetchPersonalPlaylist({ slugOrId: numericId, locale });
    } catch (err) {
      logger.error(err, {
        context: '[Favorites] Failed to fetch playlist with tracks',
      });
      return null;
    }
  };

  const fetchRecentlyListenedTracks = useCallback(
    async (size = defaultRecentlyListenedTracksSize, page = 1) => {
      try {
        const { count, data, totalPages } = await fetchRecentlyListened(
          locale,
          size,
          page,
        );

        setTotalRecentlyListenedTracksCount(count);
        setTotalRecentlyListenedTracksPages(totalPages);

        setRecentlyListenedTracks(prev => {
          const existing = new Set(prev.map(t => t.trackId));
          const incoming = data.filter(x => !existing.has(x.trackId));
          return page === 1 ? incoming : [...prev, ...incoming];
        });

        return { data, count, totalNumberOfPage: totalPages };
      } catch (err) {
        logger.error(err, {
          context: '[Favorites] Failed to fetch recently listened tracks',
        });
        return { data: [], count: 0, totalNumberOfPage: 0 };
      }
    },
    [locale, defaultRecentlyListenedTracksSize],
  );

  const dryHydrateFavourites = useCallback(async () => {
    if (isLatestFavouritesLoadedRef.current || isHydratingRef.current) return;
    isHydratingRef.current = true;

    try {
      setLoading(true);

      const favouriteIds = await fetchAllFavouriteIds();
      const placeHolder = Object.fromEntries(
        Object.entries(favouriteIds).map(([type, ids]) => [
          type,
          Object.fromEntries((ids as number[]).map(id => [id, {}])),
        ]),
      );

      setFavouritesMap(placeHolder as FavouriteMap);
      setUserPlaylists(await fetchUserCreatedPlaylists(locale));
    } catch (err) {
      logger.error(err, { context: '[Favorites] Dry hydrate failed' });
    } finally {
      isHydratingRef.current = false;
      setLoading(false);
    }
  }, [locale]);

  const conditionalHydrateFavourites = useCallback(
    async (favouriteIds: FavouriteIds): Promise<void> => {
      const tasks: Promise<void>[] = [];

      const has = (t: FavouriteType) => (favouriteIds[t]?.length ?? 0) > 0;

      if (has(FavouriteType.Track)) {
        tasks.push(
          fetchSavedTracks(locale).then(data => {
            setFavouritesMap(prev => ({
              ...prev,
              [FavouriteType.Track]: arrayToRecord(
                data as Array<{ id?: string | number }>,
                'id',
              ) as Record<string, FavouriteContent>,
            }));
          }),
        );

        // only fetch recents if there are track favourites (same as old behaviour)
        tasks.push(
          fetchRecentlyListenedTracks(
            defaultRecentlyListenedTracksSize,
            1,
          ).then(() => {}),
        );
      }

      if (has(FavouriteType.Album)) {
        tasks.push(
          fetchSavedAlbums(locale).then(data => {
            setFavouritesMap(prev => ({
              ...prev,
              [FavouriteType.Album]: arrayToRecord(
                data as Array<{ id?: string | number }>,
                'id',
              ) as Record<string, FavouriteContent>,
            }));
          }),
        );
      }

      if (has(FavouriteType.Playlist)) {
        tasks.push(
          fetchSavedPlaylists(locale).then(data => {
            setFavouritesMap(prev => ({
              ...prev,
              [FavouriteType.Playlist]: arrayToRecord(
                data as Array<{ id?: string | number }>,
                'id',
              ) as Record<string, FavouriteContent>,
            }));
          }),
        );
      }

      if (has(FavouriteType.Article)) {
        tasks.push(
          fetchSavedArticles(locale).then(data => {
            setFavouritesMap(prev => ({
              ...prev,
              [FavouriteType.Article]: arrayToRecord(
                data as Array<{ id?: string | number }>,
                'id',
              ) as Record<string, FavouriteContent>,
            }));
          }),
        );
      }

      if (has(FavouriteType.Video)) {
        tasks.push(
          fetchSavedVideos(locale).then(data => {
            setFavouritesMap(prev => ({
              ...prev,
              [FavouriteType.Video]: arrayToRecord(
                data as Array<{ id?: string | number }>,
                'id',
              ) as Record<string, FavouriteContent>,
            }));
          }),
        );
      }

      if (has(FavouriteType.Collection)) {
        tasks.push(
          fetchSavedCollections(locale).then(data => {
            setFavouritesMap(prev => ({
              ...prev,
              [FavouriteType.Collection]: arrayToRecord(
                data as Array<{ id?: string | number }>,
                'id',
              ) as Record<string, FavouriteContent>,
            }));
          }),
        );
      }

      await Promise.all(tasks);
      isLatestFavouritesLoadedRef.current = true;
    },
    [locale, fetchRecentlyListenedTracks, defaultRecentlyListenedTracksSize],
  );

  const hydrateFavourites = useCallback(async () => {
    if (isLatestFavouritesLoadedRef.current || isHydratingRef.current) return;
    isHydratingRef.current = true;

    try {
      setLoading(true);

      // 1) fetch ids
      const favouriteIds = await fetchAllFavouriteIds();

      // 2) put placeholders in map (fast)
      const placeHolder = Object.fromEntries(
        Object.entries(favouriteIds).map(([type, ids]) => [
          type,
          Object.fromEntries((ids as number[]).map(id => [id, {}])),
        ]),
      ) as FavouriteMap;

      setFavouritesMap(prev => ({ ...prev, ...placeHolder }));

      // 3) playlists list (independent)
      setUserPlaylists(await fetchUserCreatedPlaylists(locale));

      // 4) only fetch full objects for types that have ids
      await conditionalHydrateFavourites(favouriteIds);
    } catch (err) {
      logger.error(err, { context: '[Favorites] Hydrate failed' });
    } finally {
      isHydratingRef.current = false;
      setLoading(false);
    }
  }, [locale, conditionalHydrateFavourites]);

  const resetFavorites = useCallback(() => {
    setFavouritesMap(() =>
      Object.values(FavouriteType).reduce((acc, type) => {
        acc[type] = {};
        return acc;
      }, {} as FavouriteMap),
    );

    setUserPlaylists([]);
    setRecentlyListenedTracks([]);
    setTotalRecentlyListenedTracksCount(0);
    setTotalRecentlyListenedTracksPages(0);
    isLatestFavouritesLoadedRef.current = false;
  }, []);

  useEffect(() => {
    if (isAuthenticated) requestAnimationFrame(() => void hydrateFavourites());
    else resetFavorites();
  }, [hydrateFavourites, isAuthenticated, resetFavorites]);

  const favourites: FavouriteList = Object.values(FavouriteType).reduce(
    (acc, type) => {
      const arr = recordToArray(favouritesMap[type] ?? {});
      if (arr.length > 0) acc[type] = arr as FavouriteContent[];
      return acc;
    },
    {} as FavouriteList,
  );

  const initialRecentlyListened = {
    items: recentlyListenedTracks,
    count: totalRecentlyListenedTracksCount,
    totalNumberOfPage: totalRecentlyListenedTracksPages,
  };

  const getLibraryFilters = async (): Promise<TabConfig[]> => {
    const filters: TabConfig[] = [
      {
        key: FavoritesFilter.All,
        label: t('B2B_LIBRARY_FILTER_ALL_CONTENT'),
        icon: <HeroIcon icon={HeartIcon} className="w-5 h-5" />,
      },
    ];

    if (Object.keys(favouritesMap.video ?? {}).length > 0) {
      filters.push({
        key: FavoritesFilter.Videos,
        label: t('B2B_LIBRARY_FILTER_VIDEOS'),
        icon: <HeroIcon icon={FilmIcon} className="w-5 h-5" />,
      });
    }

    if (
      Object.keys(favouritesMap.track ?? {}).length > 0 ||
      Object.keys(favouritesMap.album ?? {}).length > 0 ||
      Object.keys(favouritesMap.playlist ?? {}).length > 0 ||
      userPlaylists.length > 0 ||
      recentlyListenedTracks.length > 0
    ) {
      filters.push({
        key: FavoritesFilter.Music,
        label: t('B2B_LIBRARY_FILTER_MUSIC'),
        icon: <HeroIcon icon={PlayCircleIcon} className="w-5 h-5" />,
      });
    }

    if (Object.keys(favouritesMap.collection ?? {}).length > 0) {
      filters.push({
        key: FavoritesFilter.Collections,
        label: t('B2B_LIBRARY_FILTER_COLLECTIONS'),
        icon: <HeroIcon icon={ArchiveBoxIcon} className="w-5 h-5" />,
      });
    }

    if (Object.keys(favouritesMap.article ?? {}).length > 0) {
      filters.push({
        key: FavoritesFilter.Articles,
        label: t('B2B_LIBRARY_FILTER_ARTICLES'),
        icon: <HeroIcon icon={BookOpenIcon} className="w-5 h-5" />,
      });
    }

    return filters;
  };

  const fetchLibrarySections = (
    filter: FavoritesFilter = FavoritesFilter.All,
  ): Partial<librarySectionsMap> => {
    const librarySections = Object.values(FavouriteType).reduce((acc, type) => {
      const arr = recordToArray(favouritesMap[type] ?? {});
      if (arr.length > 0) acc[type] = arr as unknown as LibraryContent[];

      if (type === FavouriteType.Track) {
        acc[type] = buildTrackVirtualPlaylists(
          t,
          recordToArray(favouritesMap.track ?? {}).length > 0,
          recentlyListenedTracks.length > 0,
        ) as unknown as LibraryContent[];
      }
      return acc;
    }, {} as librarySectionsMap);

    if (userPlaylists.length > 0) {
      librarySections['personal-playlists'] = buildPersonalPlaylists(
        userPlaylists,
      ) as unknown as LibraryContent[];
    }

    switch (filter) {
      case FavoritesFilter.All:
        return librarySections;
      case FavoritesFilter.Videos:
        return { video: librarySections.video || [] };
      case FavoritesFilter.Music:
        return {
          track: librarySections.track || [],
          album: librarySections.album || [],
          playlist: librarySections.playlist || [],
          'personal-playlists': librarySections['personal-playlists'] || [],
        };
      case FavoritesFilter.Articles:
        return { article: librarySections.article || [] };
      case FavoritesFilter.Collections:
        return { collection: librarySections.collection || [] };
      default:
        return librarySections;
    }
  };

  return (
    <FavoritesContext.Provider
      value={{
        loading,
        favourites, // existing API (arrays)
        favouritesMap, // ✅ new API (map)
        recentlyListenedTracks: initialRecentlyListened,
        fetchLibrarySections,
        getLibraryFilters,
        filterFavourites: (type: FavouriteType) => ({
          [type]: recordToArray(
            favouritesMap[type] ?? {},
          ) as FavouriteContent[],
        }),
        isFavourite,
        toggleFavourite,
        fetchRecentlyListenedTracks,
        userPlaylists,
        fetchUserPlaylists,
        addTrackToUserPlaylist,
        removeTrackFromUserPlaylist,
        createUserPlaylistAndStore,
        renameUserPlaylistAndStore,
        deleteUserPlaylistAndStore,
        hydrateFavourites,
        dryHydrateFavourites,
        refreshSpecificFavouriteType,
        reorderPlaylistTracksAndStore,
        fetchPersonalPlaylistWithTracks,
      }}
    >
      {children}
    </FavoritesContext.Provider>
  );
}
